// Square-image picker used for tournament covers (create form) and company
// logos (profile page). Lets the user choose an image, crops it to a
// COVER_SIZE square when it is not already exactly that size (see the project
// requirement: covers larger than 600×600 must be cropped before save), and
// surfaces the resulting blob to the parent via `onChange`. The parent owns
// the blob; this component owns the transient preview/crop URLs. An already
// stored image can be shown via `existingUrl` until a new one is picked;
// `onChange(null)` means "no image" (both a cleared pick and a removed
// existing image).

import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { COVER_SIZE } from '../domain';
import { useTranslation } from '../state/I18nContext';
import { imageDimensions } from './cropImage';
import { CoverCropDialog } from './CoverCropDialog';

/** Image MIME types accepted for covers (must match the server's allow-list). */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

interface CoverImagePickerProps {
  blob: Blob | null;
  onChange: (blob: Blob | null) => void;
  disabled?: boolean;
  /** Field label; defaults to the tournament-cover wording. */
  label?: string;
  /** Helper text under the buttons; defaults to the tournament-cover wording. */
  caption?: string;
  /** Public URL of an already stored image, previewed until a new pick. */
  existingUrl?: string | null;
}

export function CoverImagePicker({
  blob,
  onChange,
  disabled,
  label,
  caption,
  existingUrl = null,
}: CoverImagePickerProps) {
  const { t } = useTranslation();
  const labelText = label ?? t('admin.cover.defaultLabel');
  const captionText = caption ?? t('admin.cover.defaultCaption');
  const inputRef = useRef<HTMLInputElement>(null);
  // Object URL of an image awaiting crop; non-null opens the crop dialog.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live preview of the currently-selected image, revoked when it changes.
  const previewUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Revoke the crop source URL whenever it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  // A fresh pick wins over the stored image; the stored one shows until then.
  const previewSrc = previewUrl ?? existingUrl;

  const pickFile = () => {
    setError(null);
    inputRef.current?.click();
  };

  const onFileSelected = async (file: File) => {
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t('admin.cover.errors.unsupportedType'));
      return;
    }
    const src = URL.createObjectURL(file);
    try {
      const { width, height } = await imageDimensions(src);
      // Already the canonical size: use the file as-is, no crop needed.
      if (width === COVER_SIZE && height === COVER_SIZE) {
        URL.revokeObjectURL(src);
        onChange(file);
        return;
      }
      // Otherwise crop/normalize to a COVER_SIZE square.
      setCropSrc(src);
    } catch {
      URL.revokeObjectURL(src);
      setError(t('admin.cover.errors.unreadable'));
    }
  };

  const onCropped = (cropped: Blob) => {
    onChange(cropped);
    setCropSrc(null);
  };

  const remove = () => {
    setError(null);
    onChange(null);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {labelText}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so re-selecting the same file still fires onChange.
          e.target.value = '';
          if (file) void onFileSelected(file);
        }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: 2,
            flexShrink: 0,
            border: '1px dashed',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            backgroundImage: previewSrc ? `url(${previewSrc})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {!previewSrc && (
            <AddPhotoAlternateIcon sx={{ color: 'text.disabled' }} />
          )}
        </Box>
        <Stack spacing={1} alignItems="flex-start">
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={pickFile}
            disabled={disabled}
          >
            {previewSrc
              ? t('admin.cover.changeImage')
              : t('admin.cover.addImage', { label: labelText.toLowerCase() })}
          </Button>
          {previewSrc && (
            <Button
              variant="text"
              size="small"
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              onClick={remove}
              disabled={disabled}
            >
              {t('admin.cover.remove')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('admin.cover.savedAsSquare', { size: COVER_SIZE })} {captionText}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {cropSrc && (
        <CoverCropDialog
          open
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onCropped={onCropped}
        />
      )}
    </Box>
  );
}
